'use client';
import { useTenant } from '@/contexts/TenantContext';
import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import type {
  Installation,
  LandscapeDisplay,
  LandscapeImmersive,
  LandscapeDescription,
  LandscapeChapterHero,
  LandscapeChapter,
  LandscapeProcess,
  LandscapeSketchbook,
  LandscapeVisionDisplay,
  LandscapeDocumentary,
  LandscapeRenders,
  LandscapePress,
  LandscapeGallery,
  LandscapeExhibition,
  LandscapeEmbed
} from '@/types';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { Common_HeroSection } from '../landscape/CommonHeroSection';
import { EntryInformation, EntryPhotos, readLayout, type EntrySpec } from '../landscape/EntryInformation';
import { Landscape_Documentary } from '../landscape/LandscapeDocumentary';
import { rowColumns, rowFrame } from '../landscape/documentary-row';
import { Landscape_Renders } from '../landscape/LandscapeRender';
import { Landscape_Gallery } from '../landscape/LandscapeGallery';
import { Landscape_Press } from '../landscape/LandscapePress';
import { Landscape_Exhibition } from '../landscape/LandscapeExhibition';
import { Landscape_Embed } from '../landscape/LandscapeEmbed';
import { firstMediaData, getMediaData } from '@/lib/utils/media.utils';
import { RoomStage, roomFor } from './rooms';
import { opensRoom } from '@/components/ui/rooms/resolve';

interface InstallationProps {
  detail: Installation;
}


/** Splits a layout into runs: consecutive documentary blocks form one run, every other block its own. */
function groupDocumentaries<T extends { blockType: string }>(layout: T[]): T[][] {
  const groups: T[][] = [];
  for (const block of layout) {
    const last = groups[groups.length - 1];
    if (block.blockType === 'LandscapeDocumentary' && last && last[0].blockType === 'LandscapeDocumentary') last.push(block);
    else groups.push([block]);
  }
  return groups;
}

export default function InstallationDetailPage({ detail, startRoom = false }: InstallationProps & { startRoom?: boolean }) {
  // Etiketleri dönüştür
  const tags: string[] = [];

  // Etiketleri ekle
  if (detail.tags && Array.isArray(detail.tags)) {
    detail.tags.forEach(tag => {
      if (typeof tag === 'number') {
        tags.push(String(tag));
      } else if (tag && typeof tag === 'object' && 'title' in tag) {
        tags.push(tag.title);
      }
    });
  }
  // An installation that is also a room on the site keeps the plain white header every catalogue
  // page has, and plays the room itself, live, straight under it and above the information.
  // Circular Repetition is one of them: the registry knows it as the portal room.
  const { isOrkhan } = useTenant();
  const room = roomFor('installations', detail, { handBuilt: isOrkhan });
  const cover = room ? firstMediaData(detail.coverImage, detail.cardImage, detail.meta?.image).url || undefined : undefined;
  // The stage takes the place of the entry's older immersive block where it has one, and of the
  // first still of the artwork otherwise.
  const stageBlock = room
    ? detail.layout.find(block => block.blockType === 'LandscapeImmersive') ?? detail.layout.find(block => block.blockType === 'LandscapeDisplay')
    : undefined;
  const stageStill = stageBlock?.blockType === 'LandscapeImmersive' ? stageBlock.posterImage : stageBlock?.blockType === 'LandscapeDisplay' ? stageBlock.image : undefined;
  const year = detail.year || new Date(detail.createdAt).getFullYear();
  if (year) tags.push(year.toString());
  if (detail.medium) {
    const mediumWords = detail.medium.split(' ');
    const truncatedMedium = mediumWords.slice(0, 3).join(' ');
    tags.push(truncatedMedium);
  }

  // What the entry knows about itself. Shown by the description block where the layout has
  // one, and by the page itself where it does not, so no entry is left as a bare title.
  const specs: EntrySpec[] = [];

  if (detail.medium) {
    specs.push({
      label: 'Medium',
      specifications: [{ value: detail.medium }],
    });
  }
  // dimensions
  if (detail.dimensions) {
    specs.push({
      label: 'Dimensions',
      specifications: [{ value: detail.dimensions }],
    });
  }
  // curator
  if (detail.curator) {
    specs.push({
      label: 'Curator',
      specifications: [{ value: detail.curator }],
    });
  }
  // location
  if (detail.location) {
    specs.push({
      label: 'Location',
      specifications: [{ value: detail.location }],
    });
  }
  // year
  if (detail.year) {
    specs.push({
      label: 'Year',
      specifications: [{ value: detail.year.toString() }],
    });
  }
  // materials array
  if (detail.materials && Array.isArray(detail.materials) && detail.materials.length > 0) {
    specs.push({
      label: 'Materials',
      specifications: detail.materials.map(material => ({
        value: material.material,
      })),
    });
  }
  const page = readLayout(detail.layout);

  const GetLayout = ({
    layout,
  }: {
    layout:
      | LandscapeDisplay
      | LandscapeImmersive
      | LandscapeVisionDisplay
      | LandscapeDescription
      | LandscapeChapterHero
      | LandscapeChapter
      | LandscapeProcess
      | LandscapeSketchbook
      | LandscapeDocumentary
      | LandscapeRenders
      | LandscapeGallery
      | LandscapePress
      | LandscapeExhibition
      | LandscapeEmbed;
  }) => {
    switch (layout.blockType) {
      case 'LandscapeDisplay':
        return <Landscape_Display layout={layout} />;
      case 'LandscapeImmersive':
        return <Landscape_Immersive layout={layout} />;
      case 'LandscapeChapterHero':
        return <Landscape_ChapterHero layout={layout} />;
      case 'LandscapeChapter':
        return <Landscape_Chapter layout={layout} />;
      case 'LandscapeProcess':
        return <Landscape_Process layout={layout} />;
      case 'LandscapeSketchbook':
        return <Landscape_Sketchbook layout={layout} />;
      case 'LandscapeDocumentary':
        return <Landscape_Documentary layout={layout} />;
      case 'LandscapeRenders':
        return <Landscape_Renders layout={layout} />;
      case 'LandscapePress':
        return <Landscape_Press layout={layout} />;
      case 'LandscapeGallery':
        return <Landscape_Gallery layout={layout} />;
      case 'LandscapeExhibition':
        return <Landscape_Exhibition layout={layout} />;
      case 'LandscapeEmbed':
        return <Landscape_Embed layout={layout} />;
      case 'LandscapeDescription':
        // The stage above is the way into the room; a second link to it in the text is left out.
        return <Landscape_Description layout={room && opensRoom(layout.url) ? { ...layout, url: null, urlTitle: null } : layout} specs={specs} />;
      default:
        return null; // Eşleşme yoksa null döndür
    }
  };

  // Content area - ensure overflow works with parent container
  return (
    <div
      className="w-full bg-black relative h-full overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
      id="landscape-content"
    >
      {/* Hero Section */}
      <Common_HeroSection
        title={detail.title}
        description={detail.description}
        leftTop={detail.location || ''}
        rightTop={detail.year.toString() || ''}
        tags={tags}
      />

      {/* A work with a room shows the room first, straight under its title. */}
      {room ? <RoomStage room={room} poster={getMediaData(stageStill).url || cover} autoStart={startRoom} /> : null}

      {/* An entry with no description block still shows what it knows. */}
      {page.describes ? null : <EntryInformation specs={specs} />}

      {/* Installation Sections. Documentary films that follow one another sit side by side. */}
      {groupDocumentaries(detail.layout).map((group, index) =>
        group.length > 1 ? (
          <div key={index} className={`grid grid-cols-1 ${rowColumns(group.length)} gap-x-[12px]`}>
            {group.map((lay, i) => (
              <Landscape_Documentary key={i} layout={lay as LandscapeDocumentary} inRow frame={rowFrame(group as LandscapeDocumentary[])} />
            ))}
          </div>
        ) : room && stageBlock && group[0] === stageBlock ? null : (
          <GetLayout key={index} layout={group[0]} />
        ),
      )}
      {/* The entry's own photographs, where the page shows no pictures or film of its own. */}
      {page.showsMedia ? null : <EntryPhotos photos={detail.photos} title={detail.title} />}
    </div>
  );
}
